import { useState, useCallback } from 'react';
import API_ROUTES from './apiRoutes';
import { useAuth } from './useAuth';
import { SignupData } from '../types';

interface UseSignupReturn {
  signup: (data: SignupData) => void;
  success: boolean;
  loading: boolean;
  error: string | null;
}

export const useSignup = (): UseSignupReturn => {
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { login: authLogin } = useAuth();

  const signup = useCallback(async (data: SignupData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ROUTES.AUTH.signup(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          password: data.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erro ao criar conta');
      }

      const result = await response.json().catch(() => ({}));
      
      // Salvar email no localStorage
      localStorage.setItem('userEmail', data.email);
      
      // Se a API retornar tokens automaticamente após signup
      if (result.access_token && result.refresh_token) {
        authLogin(result.access_token, result.refresh_token);
      } else {
        // Se não retornar tokens, fazer login automático
        try {
          const loginResponse = await fetch(API_ROUTES.AUTH.login(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: data.email,
              password: data.password,
            }),
          });

          if (loginResponse.ok) {
            const loginResult = await loginResponse.json();
            authLogin(loginResult.access_token, loginResult.refresh_token);
          }
        } catch (loginError) {
        }
      }
      
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [authLogin]);

  return { signup, success, loading, error };
};
